import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Branch as DismissableLayerBranch } from '@radix-ui/react-dismissable-layer';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Custom Select — API-compatible with shadcn/Radix Select.
 *
 * SelectContent is rendered through a Portal anchored to the trigger's rect,
 * so the dropdown is never visually clipped by ancestor `overflow-hidden` (cards,
 * dialogs, tab containers, scroll regions). Position recomputes on window scroll
 * (capture), resize, and trigger resize. Falls back above the trigger when there
 * is not enough room below.
 */

interface SelectContextType {
  value?: string;
  onValueChange?(value: string): void;
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled?: boolean;
  labels: Map<string, React.ReactNode>;
  contentId: string;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
}

const SelectContext = React.createContext<SelectContextType | null>(null);

const useSelectContext = (): SelectContextType => {
  const ctx = useContext(SelectContext);
  if (!ctx) throw new Error('Select.* components must be used inside a <Select>');
  return ctx;
};

// Walk children tree to extract (value, label) pairs for SelectValue rendering
const collectLabels = (nodes: React.ReactNode, map: Map<string, React.ReactNode>): void => {
  React.Children.forEach(nodes, (child) => {
    if (!React.isValidElement(child)) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const type: any = child.type;
    const typeName = (type && (type.displayName || type.name)) as string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const props = child.props as any;
    if (typeName === 'SelectItem' && props && props.value !== undefined) {
      map.set(String(props.value), props.children);
    }
    if (props && props.children) {
      collectLabels(props.children, map);
    }
  });
};

/* ---------- Select (root) ---------- */

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?(value: string): void;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  value,
  defaultValue,
  onValueChange,
  open: openProp,
  onOpenChange,
  disabled,
  children,
}) => {
  const [openInternal, setOpenInternal] = useState(false);
  const [valueInternal, setValueInternal] = useState<string | undefined>(defaultValue);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentId = React.useId();

  const isControlledValue = value !== undefined;
  const currentValue = isControlledValue ? value : valueInternal;
  const isControlledOpen = openProp !== undefined;
  const currentOpen = isControlledOpen ? openProp! : openInternal;

  const setOpen = useCallback(
    (o: boolean) => {
      if (!isControlledOpen) setOpenInternal(o);
      onOpenChange?.(o);
    },
    [isControlledOpen, onOpenChange]
  );

  const handleValueChange = useCallback(
    (v: string) => {
      if (!isControlledValue) setValueInternal(v);
      onValueChange?.(v);
    },
    [isControlledValue, onValueChange]
  );

  const labels = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    collectLabels(children, map);
    return map;
  }, [children]);

  useEffect(() => {
    if (!currentOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target) ?? false;
      const insideContent = contentRef.current?.contains(target) ?? false;
      if (!insideWrapper && !insideContent) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [currentOpen, setOpen]);

  return (
    <SelectContext.Provider
      value={{
        value: currentValue,
        onValueChange: handleValueChange,
        open: currentOpen,
        setOpen,
        disabled,
        labels,
        contentId,
        triggerRef,
        contentRef,
      }}
    >
      <div ref={wrapperRef} className="relative inline-block w-full">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

/* ---------- SelectGroup ---------- */

export const SelectGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

/* ---------- SelectValue ---------- */

interface SelectValueProps {
  placeholder?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}
export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, className, children }) => {
  const { value, labels } = useSelectContext();
  if (children !== undefined && children !== null && children !== false) {
    return (
      <span className={cn('truncate text-left pointer-events-none', className)}>{children}</span>
    );
  }
  const label = value !== undefined && labels.has(value) ? labels.get(value) : null;
  return (
    <span
      className={cn(
        'truncate text-left pointer-events-none',
        !label && 'text-heritage-grey',
        className
      )}
    >
      {label ?? placeholder}
    </span>
  );
};

/* ---------- SelectTrigger ---------- */

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => {
  const { open, setOpen, disabled, contentId, triggerRef } = useSelectContext();

  // Merge consumer ref with the context triggerRef so SelectContent can read
  // getBoundingClientRect() from the actual button node.
  const setRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ref, triggerRef]
  );

  return (
    <button
      ref={setRef}
      type="button"
      aria-haspopup="listbox"
      aria-controls={contentId}
      disabled={disabled}
      onClick={() => !disabled && setOpen(!open)}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px] text-text-grey transition-colors',
        'hover:border-heritage-grey focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red',
        'disabled:cursor-not-allowed disabled:opacity-50',
        open && 'border-hero-red ring-2 ring-hero-red/30',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={cn(
          'h-4 w-4 text-heritage-grey transition-transform flex-shrink-0 ml-2',
          open && 'rotate-180'
        )}
      />
    </button>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

/* ---------- SelectContent ---------- */

interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: 'popper' | 'item-aligned';
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  collisionPadding?: number;
}

interface ComputedPosition {
  top: number;
  left: number;
  width: number;
  placement: 'top' | 'bottom';
  maxHeight: number;
  viewportH: number;
}

const VIEWPORT_PADDING = 8;
const TRIGGER_GAP = 4;
const MIN_DROPDOWN_HEIGHT = 160;
// Upper bound matches the most permissive existing consumer (MultiSelect's
// max-h-[24rem]) so dropdowns do not grow taller than today in tall viewports.
const MAX_DROPDOWN_HEIGHT = 384;

export const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  (
    { className, children, position, side, align, sideOffset, collisionPadding, style, onWheel, ...props },
    ref
  ) => {
    const { open, contentId, triggerRef, contentRef } = useSelectContext();
    // Radix-API shape we accept but don't need (custom popper).
    void position;
    void side;
    void align;
    void sideOffset;
    void collisionPadding;

    const [pos, setPos] = useState<ComputedPosition | null>(null);

    const compute = useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const spaceBelow = viewportH - rect.bottom - VIEWPORT_PADDING - TRIGGER_GAP;
      const spaceAbove = rect.top - VIEWPORT_PADDING - TRIGGER_GAP;
      // Prefer below; flip above when below is cramped and above has more room.
      const preferAbove = spaceBelow < MIN_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
      const placement: 'top' | 'bottom' = preferAbove ? 'top' : 'bottom';
      const availableSpace = placement === 'bottom' ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(
        MAX_DROPDOWN_HEIGHT,
        Math.max(MIN_DROPDOWN_HEIGHT, availableSpace)
      );
      const top =
        placement === 'bottom' ? rect.bottom + TRIGGER_GAP : rect.top - TRIGGER_GAP;
      setPos({
        top,
        left: rect.left,
        width: rect.width,
        placement,
        maxHeight,
        viewportH,
      });
    }, [triggerRef]);

    useLayoutEffect(() => {
      if (!open) {
        setPos(null);
        return;
      }
      compute();
    }, [open, compute]);

    useEffect(() => {
      if (!open) return;
      const onScroll = () => compute();
      const onResize = () => compute();
      const scrollOpts: AddEventListenerOptions = { capture: true, passive: true };
      window.addEventListener('scroll', onScroll, scrollOpts);
      window.addEventListener('resize', onResize);

      let ro: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined' && triggerRef.current) {
        ro = new ResizeObserver(() => compute());
        ro.observe(triggerRef.current);
      }
      return () => {
        window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
        window.removeEventListener('resize', onResize);
        ro?.disconnect();
      };
    }, [open, compute, triggerRef]);

    // When inside a Radix Dialog (or any FocusScope-trapped layer), focusin
    // events that bubble to document trigger FocusScope to yank focus back
    // into the dialog. That kills typing in SearchableSelect/MultiSelect
    // search inputs and any focusable item inside the portaled dropdown.
    // Stopping focusin at the portal node keeps focus where the user put it.
    useEffect(() => {
      if (!open) return;
      const node = contentRef.current;
      if (!node) return;
      const stopFocusIn = (e: FocusEvent) => e.stopPropagation();
      node.addEventListener('focusin', stopFocusIn);
      return () => node.removeEventListener('focusin', stopFocusIn);
    }, [open, contentRef]);

    // Merge consumer ref with the context contentRef so outside-click detection
    // in the root can treat clicks inside the portaled content as "inside".
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref, contentRef]
    );

    const handleWheel = useCallback(
      (event: React.WheelEvent<HTMLDivElement>) => {
        onWheel?.(event);
        if (event.defaultPrevented) return;

        const content = contentRef.current;
        if (!content || content.scrollHeight <= content.clientHeight) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        content.scrollTop += event.deltaY;
      },
      [contentRef, onWheel]
    );

    if (!open || !pos) return null;
    if (typeof document === 'undefined') return null;

    const portalStyle: React.CSSProperties = {
      position: 'fixed',
      left: pos.left,
      width: pos.width,
      maxHeight: pos.maxHeight,
      // Radix Dialog sets `body { pointer-events: none }` while a modal layer
      // is open. We portal to body, so opt back in explicitly to stay clickable.
      pointerEvents: 'auto',
      ...(pos.placement === 'bottom'
        ? { top: pos.top }
        : { bottom: pos.viewportH - pos.top }),
      ...style,
    };

    // DismissableLayerBranch registers this node in the nearest Radix
    // DismissableLayer's `branches` set, so pointerdown/focus inside the
    // dropdown is treated as "inside" the parent Dialog (no auto-dismiss).
    // Outside any DismissableLayer the context is a no-op, so this is safe.
    return createPortal(
      <DismissableLayerBranch
        ref={setRef}
        id={contentId}
        style={portalStyle}
        onWheel={handleWheel}
        className={cn(
          'z-[60] overflow-y-auto rounded-md border border-[#E2E5EA] bg-white shadow-lg py-1',
          className
        )}
        {...props}
      >
        {children}
      </DismissableLayerBranch>,
      document.body
    );
  }
);
SelectContent.displayName = 'SelectContent';

/* ---------- SelectLabel ---------- */

export const SelectLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <div
    className={cn(
      'px-3 py-1.5 text-[11px] font-semibold text-heritage-grey uppercase tracking-wider',
      className
    )}
  >
    {children}
  </div>
);

/* ---------- SelectItem ---------- */

interface SelectItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
  disabled?: boolean;
}
const SelectItemInner: React.FC<SelectItemProps> = ({
  value: itemValue,
  children,
  className,
  disabled,
  ...props
}) => {
  const { value, onValueChange, setOpen } = useSelectContext();
  const isSelected = value === itemValue;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onValueChange?.(itemValue);
        setOpen(false);
      }}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors',
        'hover:bg-page-bg disabled:opacity-50 disabled:cursor-not-allowed',
        isSelected ? 'text-blueprint-blue font-medium bg-page-bg' : 'text-text-grey',
        className
      )}
      {...props}
    >
      <Check
        className={cn(
          'h-4 w-4 flex-shrink-0',
          isSelected ? 'text-hero-red' : 'opacity-0'
        )}
      />
      <span className="truncate">{children}</span>
    </button>
  );
};
SelectItemInner.displayName = 'SelectItem';
export const SelectItem = SelectItemInner;

/* ---------- SafeSelect (backwards-compat) ---------- */

interface SafeSelectProps {
  value?: string;
  onValueChange?(value: string): void;
  children: React.ReactNode;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?(open: boolean): void;
}
export const SafeSelect: React.FC<SafeSelectProps> = (props) => <Select {...props} />;
